import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../services/api';
import { getSchemaDates, getTodaysDate } from '../services/UtilityServices';
import {
  useIsAuthorized, useImpersonatedUserId, useStation,
  useMonthlyReportNames, useDailyReportNames, useUserInfo
} from '../contexts/GlobalStateContext';
import StationValjare from '../common/StationValjare';
import BattenburgLoader from '../common/BattenburgLoader';
import { toast } from 'react-toastify';

// ─────────────────────────────────────────────
// Excel-rapporter
// Period: Dag (dropdown med tillgängliga datum) eller Månad (dropdown)
// Stationer: flerval via StationValjare
// En Excel-fil per vald station laddas ned
// ─────────────────────────────────────────────
const Excel = () => {
  const { isAuthorized } = useIsAuthorized();
  const { impersonatedUserId } = useImpersonatedUserId();
  const { station } = useStation();
  const { monthlyReportNames, setMonthlyReportNames } = useMonthlyReportNames();
  const { dailyReportNames, setDailyReportNames } = useDailyReportNames();
  const { userInfo } = useUserInfo();

  const [mode, setMode] = useState('dag');        // 'dag' | 'manad'
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [allStationer, setAllStationer] = useState([]);
  const [selectedStationer, setSelectedStationer] = useState([]);
  const [loading, setLoading] = useState(false);

  const isMissingDailyData  = dailyReportNames.length > 0 && dailyReportNames[0] === 'Underlag saknas';
  const isMissingMonthlyData = monthlyReportNames.length > 0 && monthlyReportNames[0] === 'Underlag saknas';

  // ── Ladda alla stationer + rapport-datum ────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchWithAuth('/api/reports/allastationer');
        const d = await res.json();
        const filtered = d.filter(s => s !== '---').sort();
        setAllStationer(filtered);
        setSelectedStationer(filtered);
      } catch {
        toast.error('Kunde inte ladda stationer');
      }
    };
    load();
  }, []);

  // ── Ladda dagnamn när station är känd ───────────────────────────
  useEffect(() => {
    if (!station || station === '---') return;
    const loadDays = async () => {
      try {
        const dates = await getSchemaDates(station);
        if (dates.length === 0) {
          setDailyReportNames(['Underlag saknas']);
          setSelectedDay('');
          return;
        }
        setDailyReportNames(dates);
        const today = getTodaysDate();
        setSelectedDay(dates.includes(today) ? today : dates[0]);
      } catch {
        console.error('Kunde inte ladda dagdatum');
      }
    };
    loadDays();
  }, [station]);

  // ── Ladda månadsnamn när station är känd ────────────────────────
  useEffect(() => {
    if (!station || station === '---') return;
    fetchWithAuth(`/api/reports/names?type=manad&station=${encodeURIComponent(station)}`)
      .then(r => r.json())
      .then(d => setMonthlyReportNames(d.length > 0 ? d : ['Underlag saknas']))
      .catch(() => {});
  }, [station]);

  // ── Nedladdning ─────────────────────────────────────────────────
  const canDownload =
    selectedStationer.length > 0 &&
    (mode === 'dag'
      ? !!selectedDay && !isMissingDailyData
      : !!selectedMonth && !isMissingMonthlyData);

  const downloadExcel = async () => {
    if (!canDownload) return;
    setLoading(true);
    const datum = mode === 'dag' ? selectedDay : selectedMonth;

    try {
      // Enkel station → befintlig endpoint (snabbare)
      // Flera stationer → ny multi-endpoint (en fil med alla stationer)
      const isMulti = selectedStationer.length > 1;
      let urlLink = isMulti ? '/api/reports/excel-multi' : '/api/reports/excel';
      if (impersonatedUserId) urlLink += `?userId=${impersonatedUserId}`;

      const body = isMulti
        ? JSON.stringify({ type: mode, datum, stationer: selectedStationer })
        : JSON.stringify({ type: mode, datum, station: selectedStationer[0] });

      const response = await fetchWithAuth(urlLink, {
        method: 'POST',
        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body
      });

      if (!response.ok) {
        const msg = await response.text().catch(() => '');
        toast.error(`❌ Fel vid nedladdning av Excel${msg ? ': ' + msg : ''}`);
        return;
      }

      let fileName = `Rapport_${datum}.xlsx`.replace(/[/\\?%*:|"<> ]/g, '_');
      const cd = response.headers.get('content-disposition');
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m?.[1]) fileName = m[1].replace(/['"]/g, '');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) return null;

  const inputCls = 'tw-border tw-border-gray-300 tw-rounded tw-p-2 tw-text-sm tw-font-bold';

  return (
    <div className="tw-px-4 sm:tw-px-6 lg:tw-px-8">
      <div className="tw-max-w-7xl tw-mx-auto tw-py-3">
        <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-green-500">

          {/* ── Rubrik ──────────────────────────────────────────── */}
          <div className="tw-bg-gradient-to-r tw-from-green-600 tw-to-green-700 tw-text-white tw-px-4 tw-py-3">
            <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Excel-rapporter</h3>
          </div>

          <div className="tw-p-6">
            <div className="tw-mb-6 tw-pb-5 tw-border-b tw-border-gray-200 tw-space-y-5">

              {/* ── Steg 1: Period ──────────────────────────────── */}
              <div>
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">
                  1. Välj period
                </p>

                {/* Dag / Månad-toggle */}
                <div className="tw-flex tw-gap-0 tw-mb-3 tw-w-fit tw-rounded tw-border tw-border-gray-300 tw-overflow-hidden">
                  {[['dag', 'Dag'], ['manad', 'Månad']].map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => { setMode(id); }}
                      className={`tw-px-5 tw-py-2 tw-text-sm tw-font-bold tw-transition-colors ${
                        mode === id
                          ? 'tw-bg-green-600 tw-text-white'
                          : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {mode === 'dag' ? (
                  <select
                    value={selectedDay}
                    disabled={isMissingDailyData}
                    onChange={e => setSelectedDay(e.target.value)}
                    className={`${inputCls} tw-min-w-[200px]`}
                  >
                    {!isMissingDailyData && <option value="">Välj dag</option>}
                    {dailyReportNames.map((d, i) => (
                      <option key={i} value={d}>{d}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={selectedMonth}
                    disabled={isMissingMonthlyData}
                    onChange={e => setSelectedMonth(e.target.value)}
                    className={`${inputCls} tw-min-w-[200px]`}
                  >
                    {!isMissingMonthlyData && <option value="">Välj månad</option>}
                    {monthlyReportNames
                      .filter(m => m !== 'Underlag saknas')
                      .map((m, i) => (
                        <option key={`${m}-${i}`} value={m}>{m.replace('_', ' ')}</option>
                      ))}
                  </select>
                )}

                {(mode === 'dag' ? isMissingDailyData : isMissingMonthlyData) && (
                  <p className="tw-text-xs tw-text-red-500 tw-mt-1">Underlag saknas för vald station</p>
                )}
              </div>

              {/* ── Steg 2: Stationer ───────────────────────────── */}
              <div>
                <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">
                  2. Välj stationer
                </p>
                <StationValjare
                  allStationer={allStationer}
                  value={selectedStationer}
                  onChange={setSelectedStationer}
                />
              </div>

              {/* ── Excel-knapp ─────────────────────────────────── */}
              <div className="tw-flex tw-items-center tw-gap-3">
                <button
                  onClick={downloadExcel}
                  disabled={!canDownload || loading}
                  className="tw-bg-green-700 hover:tw-bg-green-800 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-bold tw-px-8 tw-py-2 tw-rounded tw-transition-opacity"
                >
                  {loading
                    ? 'Genererar...'
                    : `⬇ Excel${selectedStationer.length > 1 ? ` (${selectedStationer.length} stationer)` : ''}`}
                </button>

                {!canDownload && !loading && (
                  <span className="tw-text-xs tw-text-gray-400">
                    {selectedStationer.length === 0
                      ? 'Välj minst en station'
                      : mode === 'dag'
                        ? 'Välj dag'
                        : 'Välj månad'}
                  </span>
                )}

                {loading && <BattenburgLoader top={-30} />}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Excel;
